import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useCallback, ReactNode, Fragment } from 'react';
import { MoreVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
export interface ColumnDef<T> {
  id: string;
  header: string;
  cell: (item: T) => ReactNode;
  className?: string;
}
export interface ActionMenuItem<T> {
  label: string;
  icon?: ReactNode;
  onClick: (item: T) => void;
  className?: string;
  /** When provided and returns false for an item, the action is hidden for that row. */
  isVisible?: (item: T) => boolean;
}
export interface PaginationConfig {
  mode: 'prev-next' | 'load-more';
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /**
   * Where to scroll after a prev/next page change.
   * - 'page': scroll to the top of the window
   * - 'table': scroll to the top of the table
   * - 'none' (default): don't scroll
   */
  scrollOnPageChange?: 'page' | 'table' | 'none';
}
interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  actions?: ActionMenuItem<T>[];
  isLoading?: boolean;
  emptyState?: {
    icon?: ReactNode;
    title: string;
    description?: string;
    compact?: boolean;
  };
  getItemId: (item: T) => number | string;
  onSelectionChange?: (selectedIds: Set<number | string>) => void;
  selectedIds?: Set<number | string>;
  className?: string;
  bulkActions?: ReactNode;
  pagination?: PaginationConfig;
  enableSelection?: boolean;
  /** Column IDs to exclude from rendering */
  excludeColumns?: string[];
  /** Render a full-width detail row beneath a data row. Return null/undefined to skip. */
  detailRow?: (item: T) => ReactNode | null | undefined;
}
export function DataTable(t0) {
  const $ = _c(104);
  const {
    data,
    columns: columnsProp,
    actions,
    isLoading,
    emptyState,
    getItemId,
    onSelectionChange,
    selectedIds: externalSelectedIds,
    className,
    bulkActions,
    pagination,
    enableSelection,
    excludeColumns,
    detailRow
  } = t0;
  let t1;
  if ($[0] !== columnsProp || $[1] !== excludeColumns) {
    t1 = excludeColumns?.length ? columnsProp.filter(col => !excludeColumns.includes(col.id)) : columnsProp;
    $[0] = columnsProp;
    $[1] = excludeColumns;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const columns = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [internalSelectedIds, setInternalSelectedIds] = useState(t2);
  const selectedIds = externalSelectedIds ?? internalSelectedIds;
  const setSelectedIds = onSelectionChange ?? setInternalSelectedIds;
  const rootRef = useRef(null);
  let t3;
  if ($[4] !== pagination?.scrollOnPageChange) {
    t3 = handler => {
      const behavior = pagination?.scrollOnPageChange ?? "none";
      if (behavior === "page") {
        window.scrollTo({
          top: 0
        });
      } else {
        if (behavior === "table") {
          rootRef.current?.scrollIntoView({
            block: "start"
          });
        }
      }
      handler?.();
    };
    $[4] = pagination?.scrollOnPageChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  pagination?.scrollOnPageChange;
  const handlePageChange = t3;
  let t4;
  if ($[6] !== selectedIds || $[7] !== setSelectedIds) {
    t4 = itemId => {
      const newSelection = new Set(selectedIds);
      if (newSelection.has(itemId)) {
        newSelection.delete(itemId);
      } else {
        newSelection.add(itemId);
      }
      setSelectedIds(newSelection);
    };
    $[6] = selectedIds;
    $[7] = setSelectedIds;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const toggleItemSelection = t4;
  let t5;
  if ($[9] !== data || $[10] !== getItemId || $[11] !== selectedIds.size || $[12] !== setSelectedIds) {
    t5 = () => {
      if (selectedIds.size === data.length) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(data.map(getItemId)));
      }
    };
    $[9] = data;
    $[10] = getItemId;
    $[11] = selectedIds.size;
    $[12] = setSelectedIds;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const toggleAllItems = t5;
  if (isLoading) {
    let t6;
    if ($[14] !== className) {
      t6 = cn("bg-white rounded-xl border border-gray-200 overflow-hidden", className);
      $[14] = className;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== enableSelection) {
      t7 = enableSelection && <th className="px-3 py-3 text-left w-12"><div className="h-5 w-5 bg-gray-200 rounded animate-pulse" /></th>;
      $[16] = enableSelection;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== columns) {
      t8 = columns.map(_temp);
      $[18] = columns;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== actions) {
      t9 = actions && actions.length > 0 && <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>;
      $[20] = actions;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== t7 || $[23] !== t8 || $[24] !== t9) {
      t10 = <thead className="bg-gray-50 border-b border-gray-200"><tr>{t7}{t8}{t9}</tr></thead>;
      $[22] = t7;
      $[23] = t8;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = [...Array(5)];
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    let t12;
    if ($[27] !== actions || $[28] !== columns || $[29] !== enableSelection || $[30] !== t11) {
      t12 = <tbody className="divide-y divide-gray-200">{t11.map((_, index) => <tr key={index} className="animate-pulse">{enableSelection && <td className="px-3 py-4"><div className="h-5 w-5 bg-gray-200 rounded" /></td>}{actions && actions.length > 0 && <td className="px-2 py-1 text-right"><div className="h-8 w-8 bg-gray-200 rounded ml-auto" /></td>}{columns.map(_temp2)}</tr>)}</tbody>;
      $[27] = actions;
      $[28] = columns;
      $[29] = enableSelection;
      $[30] = t11;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    let t13;
    if ($[32] !== t10 || $[33] !== t12) {
      t13 = <div className="overflow-x-auto"><table className="w-full">{t10}{t12}</table></div>;
      $[32] = t10;
      $[33] = t12;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    let t14;
    if ($[35] !== t13 || $[36] !== t6) {
      t14 = <div className={t6}>{t13}</div>;
      $[35] = t13;
      $[36] = t6;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    return t14;
  }
  if (data.length === 0 && emptyState) {
    if (emptyState.compact) {
      let t6;
      if ($[38] !== emptyState.description) {
        t6 = emptyState.description && <span className="text-gray-400"> — {emptyState.description}</span>;
        $[38] = emptyState.description;
        $[39] = t6;
      } else {
        t6 = $[39];
      }
      let t7;
      if ($[40] !== emptyState.title || $[41] !== t6) {
        t7 = <p className="text-sm text-gray-500 py-3">{emptyState.title}{t6}</p>;
        $[40] = emptyState.title;
        $[41] = t6;
        $[42] = t7;
      } else {
        t7 = $[42];
      }
      return t7;
    }
    let t6;
    if ($[43] !== emptyState.icon) {
      t6 = emptyState.icon && <div className="mx-auto mb-4">{emptyState.icon}</div>;
      $[43] = emptyState.icon;
      $[44] = t6;
    } else {
      t6 = $[44];
    }
    let t7;
    if ($[45] !== emptyState.title) {
      t7 = <h3 className="text-lg font-medium text-gray-900 mb-2">{emptyState.title}</h3>;
      $[45] = emptyState.title;
      $[46] = t7;
    } else {
      t7 = $[46];
    }
    let t8;
    if ($[47] !== emptyState.description) {
      t8 = emptyState.description && <p className="text-gray-600">{emptyState.description}</p>;
      $[47] = emptyState.description;
      $[48] = t8;
    } else {
      t8 = $[48];
    }
    let t9;
    if ($[49] !== t6 || $[50] !== t7 || $[51] !== t8) {
      t9 = <div className="text-center py-12">{t6}{t7}{t8}</div>;
      $[49] = t6;
      $[50] = t7;
      $[51] = t8;
      $[52] = t9;
    } else {
      t9 = $[52];
    }
    return t9;
  }
  let t6;
  if ($[53] !== className) {
    t6 = cn("bg-white rounded-xl border border-gray-200 overflow-hidden", className);
    $[53] = className;
    $[54] = t6;
  } else {
    t6 = $[54];
  }
  let t7;
  if ($[55] !== bulkActions || $[56] !== enableSelection || $[57] !== selectedIds.size || $[58] !== setSelectedIds) {
    t7 = enableSelection && selectedIds.size > 0 && <div className="px-3 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between"><div className="text-sm text-gray-700">{selectedIds.size} item{selectedIds.size !== 1 ? "s" : ""} selected</div><div className="flex items-center gap-2">{bulkActions}<Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>Clear Selection</Button></div></div>;
    $[55] = bulkActions;
    $[56] = enableSelection;
    $[57] = selectedIds.size;
    $[58] = setSelectedIds;
    $[59] = t7;
  } else {
    t7 = $[59];
  }
  let t8;
  if ($[60] !== data.length || $[61] !== enableSelection || $[62] !== selectedIds.size || $[63] !== toggleAllItems) {
    t8 = enableSelection && <th className="px-3 py-3 text-left w-12"><input type="checkbox" checked={selectedIds.size === data.length && data.length > 0} onChange={toggleAllItems} className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" /></th>;
    $[60] = data.length;
    $[61] = enableSelection;
    $[62] = selectedIds.size;
    $[63] = toggleAllItems;
    $[64] = t8;
  } else {
    t8 = $[64];
  }
  let t9;
  if ($[65] !== actions) {
    t9 = actions && actions.length > 0 && <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" />;
    $[65] = actions;
    $[66] = t9;
  } else {
    t9 = $[66];
  }
  let t10;
  if ($[67] !== columns) {
    t10 = columns.map(_temp3);
    $[67] = columns;
    $[68] = t10;
  } else {
    t10 = $[68];
  }
  let t11;
  if ($[69] !== t10 || $[70] !== t8 || $[71] !== t9) {
    t11 = <thead className="bg-gray-50 border-b border-gray-200"><tr>{t8}{t9}{t10}</tr></thead>;
    $[69] = t10;
    $[70] = t8;
    $[71] = t9;
    $[72] = t11;
  } else {
    t11 = $[72];
  }
  let t12;
  if ($[73] !== actions || $[74] !== columns || $[75] !== data || $[76] !== detailRow || $[77] !== enableSelection || $[78] !== getItemId || $[79] !== selectedIds || $[80] !== toggleItemSelection) {
    let t13;
    if ($[82] !== actions || $[83] !== columns || $[84] !== detailRow || $[85] !== enableSelection || $[86] !== getItemId || $[87] !== selectedIds || $[88] !== toggleItemSelection) {
      t13 = item => {
        const itemId_0 = getItemId(item);
        const detailContent = detailRow?.(item);
        const totalColSpan = columns.length + (enableSelection ? 1 : 0) + (actions && actions.length > 0 ? 1 : 0);
        return <Fragment key={itemId_0}><tr className={cn("hover:bg-gray-50 transition-colors", selectedIds.has(itemId_0) && "bg-primary-50")}>{enableSelection && <td className="px-3 py-4 cursor-pointer" onClick={() => toggleItemSelection(itemId_0)}><input type="checkbox" checked={selectedIds.has(itemId_0)} onChange={() => toggleItemSelection(itemId_0)} onClick={_temp4} className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" /></td>}{actions && actions.length > 0 && <td className="px-2 py-1 text-right"><DropdownMenu><DropdownMenuTrigger asChild={true}><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger><DropdownMenuContent align="start">{actions.filter(action => action.isVisible === undefined || action.isVisible(item)).map((action_0, index_0) => <DropdownMenuItem key={index_0} onClick={() => action_0.onClick(item)} className={cn("cursor-pointer", action_0.className)}>{action_0.icon && <span className="mr-2">{action_0.icon}</span>}{action_0.label}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu></td>}{columns.map(column_2 => <td key={column_2.id} className={cn("px-6 py-2.5", column_2.className)}>{column_2.cell(item)}</td>)}</tr>{detailContent && <tr className="bg-gray-50/50"><td colSpan={totalColSpan} className="px-6 py-2">{detailContent}</td></tr>}</Fragment>;
      };
      $[82] = actions;
      $[83] = columns;
      $[84] = detailRow;
      $[85] = enableSelection;
      $[86] = getItemId;
      $[87] = selectedIds;
      $[88] = toggleItemSelection;
      $[89] = t13;
    } else {
      t13 = $[89];
    }
    t12 = data.map(t13);
    $[73] = actions;
    $[74] = columns;
    $[75] = data;
    $[76] = detailRow;
    $[77] = enableSelection;
    $[78] = getItemId;
    $[79] = selectedIds;
    $[80] = toggleItemSelection;
    $[81] = t12;
  } else {
    t12 = $[81];
  }
  let t13;
  if ($[90] !== t12) {
    t13 = <tbody className="divide-y divide-gray-200">{t12}</tbody>;
    $[90] = t12;
    $[91] = t13;
  } else {
    t13 = $[91];
  }
  let t14;
  if ($[92] !== t11 || $[93] !== t13) {
    t14 = <div className="overflow-x-auto"><table className="w-full">{t11}{t13}</table></div>;
    $[92] = t11;
    $[93] = t13;
    $[94] = t14;
  } else {
    t14 = $[94];
  }
  let t15;
  if ($[95] !== handlePageChange || $[96] !== isLoading || $[97] !== pagination) {
    t15 = pagination && <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">{pagination.mode === "prev-next" ? <><Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.onPrevPage)} disabled={!pagination.hasPrevPage || isLoading} className="gap-2"><ChevronLeft className="h-4 w-4" />Previous</Button><Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.onNextPage)} disabled={!pagination.hasNextPage || isLoading} className="gap-2">Next<ChevronRight className="h-4 w-4" /></Button></> : <div className="w-full flex justify-center"><Button variant="outline" size="sm" onClick={pagination.onLoadMore} disabled={!pagination.hasNextPage || pagination.isLoadingMore} className="gap-2">{pagination.isLoadingMore ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />Loading...</> : "Load More"}</Button></div>}</div>;
    $[95] = handlePageChange;
    $[96] = isLoading;
    $[97] = pagination;
    $[98] = t15;
  } else {
    t15 = $[98];
  }
  let t16;
  if ($[99] !== t14 || $[100] !== t15 || $[101] !== t6 || $[102] !== t7) {
    t16 = <div ref={rootRef} className={t6}>{t7}{t14}{t15}</div>;
    $[99] = t14;
    $[100] = t15;
    $[101] = t6;
    $[102] = t7;
    $[103] = t16;
  } else {
    t16 = $[103];
  }
  return t16;
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(column_1) {
  return <th key={column_1.id} className={cn("px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", column_1.className)}>{column_1.header}</th>;
}
function _temp2(column_0) {
  return <td key={column_0.id} className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-3/4" /></td>;
}
function _temp(column) {
  return <th key={column.id} className={cn("px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", column.className)}>{column.header}</th>;
}